import type { PublishingAccount } from '../../types/publishing';
import { cn } from '../../lib/utils';
import type { MenuGroupMode } from '../../lib/publishing/calendarSelection';
import { MultiAccountPanel } from './MultiAccountPanel';

// Publishing calendar/drafts — the left-rail account selector (2026-07-29): a
// "Group by" switch (Brand · Group · No Group) that ONLY reorganises the menu,
// over the multi-select MultiAccountPanel. Shared by the calendar and the drafts
// page (both scope to the same session-persisted selection). The switch is a pure
// display control — it never filters the calendar or changes the grid.

const GROUP_OPTIONS: { value: MenuGroupMode; label: string }[] = [
  { value: 'brand', label: 'Brand' },
  { value: 'group', label: 'Group' },
  { value: 'flat', label: 'No Group' },
];

export function PublishingAccountRail({
  accounts,
  selectedIds,
  onChange,
  groupMode,
  onGroupModeChange,
  loading,
}: {
  accounts: PublishingAccount[];
  selectedIds: Set<string>;
  onChange: (next: Set<string>) => void;
  groupMode: MenuGroupMode;
  onGroupModeChange: (mode: MenuGroupMode) => void;
  loading?: boolean;
}) {
  return (
    <div className="flex w-full shrink-0 flex-col gap-2">
      <div>
        <span className="mb-1 block text-xs font-medium text-slate-500">Group by</span>
        <div className="inline-flex rounded-md border border-slate-200 p-0.5">
          {GROUP_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => onGroupModeChange(o.value)}
              aria-pressed={groupMode === o.value}
              className={cn(
                'rounded px-2 py-0.5 text-xs',
                groupMode === o.value
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:bg-slate-100',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading accounts…</p>
      ) : (
        <MultiAccountPanel
          accounts={accounts}
          selectedIds={selectedIds}
          onChange={onChange}
          groupMode={groupMode}
        />
      )}
    </div>
  );
}
